import axios, { AxiosResponse } from 'axios'
import { Context } from '@nuxt/types/app'
import { APIRequest } from '@/infrastructure/network/APIRequest'
import { APIError, CustomError } from '@/infrastructure/network/APIError'

export class APIClient {
  baseURL: string = ''
  timeout: number = 120 * 1000

  constructor(private ctx: Context) {
    const host = process.server ? process.env.LOCALHOST : process.env.OPENAI_API
    this.baseURL = `${host}`
  }

  async *stream<U>(request: APIRequest<U>) {
    const abortController = new AbortController()
    try {
      const completion = await fetch(this.baseURL + request.path, {
        headers: this.createHeaders(request),
        method: 'POST',
        body: JSON.stringify(request.params),
        signal: abortController.signal
      })

      const reader = completion.body?.getReader()
      if (completion.status !== 200 || !reader) {
        throw new CustomError({
          statusCode: completion.status,
          message: 'テキストの文量が多いため処理を中断しました。\n文量を減らして再度お試しください。'
        })
      }
      const decoder = new TextDecoder('utf-8')
      let done = false
      let chunk = ''
      try {
        while (!done) {
          const { done: readDone, value } = await reader.read()
          if (readDone) {
            done = readDone
            reader.releaseLock()
            break
          }

          // dataが2個～3個含まれることがあるためパースする
          chunk = decoder.decode(value, { stream: true })
          const token = chunk
            .split('data: ')
            .map((data) => {
              const trimData = data.trim()
              if (trimData === '') return ''
              if (trimData === '[DONE]') {
                this.ctx.App.data.loading = false
                return ''
              }
              try {
                return JSON.parse(trimData)?.choices[0]?.delta?.content
              } catch (exception) {
                chunk = trimData
                return ''
              }
            })
            .filter((data) => data)
          yield token.join('')
        }
      } catch (exception) {
        console.log('stppped chat completion', exception)
      } finally {
        abortController.abort()
        reader.releaseLock()
      }
    } catch (exception) {
      this.ctx.App.data.loading = false
      this.ctx.App.state.errors = exception as APIError
    }
  }

  request<U>(request: APIRequest<U>): Promise<U> {
    return new Promise<U>((resolve, reject) => {
      axios
        .request({
          url: request.path,
          method: 'POST',
          data: this.encodedParams(request),
          timeout: this.timeout,
          baseURL: request.baseURL || this.baseURL,
          headers: this.createHeaders(request),
          maxContentLength: Infinity
        })
        .then((data) => {
          if (!process.server || !this.ctx) {
            return data
          }
          const setCookie: string[] | undefined = data.headers['set-cookie']
          if (setCookie) {
            setCookie.forEach((s) => {
              // @ts-ignore
              this.ctx.res.setHeader('set-cookie', s)
            })
          }
          return data
        })
        .then((data) => {
          const response = request.parse ? request.parse(data) : this.parse<U>(data)
          resolve(response)
        })
        .catch((err) => {
          const apiError = this.normalizeError(err)
          reject(apiError)
        })
    })
  }

  // Default parser
  private parse<U>(data: AxiosResponse): U {
    return data.data
  }

  // Convert axios error into APIError
  private normalizeError(error: any): APIError {
    return {
      statusCode: error.response && error.response.status,
      message: (error.response && (error.response.statusText || error.response.data.message)) || error.message || error.raw.message,
      data: (error.response && error.response.data) || undefined,
      errors: (error.response && error.response.data.errors) || [],
      raw: error
    }
  }

  private encodedParams(request: any) {
    if (request.contentType === 'multipart/form-data') {
      const params = new FormData()
      for (const [key, value] of Object.entries<string>(request.params)) {
        const type = Object.prototype.toString.call(value).slice(8, -1).toLowerCase()
        if (type === 'file') {
          params.append(key, value)
        } else if (value && typeof value === 'object') {
          for (const [k, v] of Object.entries<string>(value)) {
            const type = Object.prototype.toString.call(v).slice(8, -1).toLowerCase()
            if (type === 'file') {
              params.append(key + `_${k}`, v)
            } else {
              params.append(key + `_${k}`, JSON.stringify(v))
            }
          }
        } else {
          params.append(key, value)
        }
      }
      return params
    }
    return request.params
  }

  // Create headers
  private createHeaders(request: any) {
    return {
      'Content-Type': request.contentType,
      Authorization: `Bearer ${process.env.OPENAI_KEY}`
    }
  }
}
